import type { IDevice } from '@core/devices/IDevice'
import type { AxiosResponse } from 'axios'
import type { RemoteServiceResponse } from '@/netadapters/EndpointAdapter'
import { HTTP_STATUS_OK } from '@/netadapters/AbsNetAdapter'
import { TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES, retry } from '@/core/decorators/RetryDecorator'
import { isPlainObject } from 'lodash'
import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
import type { GroupFileInfo, TransmissionTaskType } from './ITask'

const DOWNLOAD_SERVICE_ID = '3000001'
const UPLOAD_SERVICE_ID = '3000002'
const TRANSMISSION_CONTROL_SERVICE_ID = '3000003'
const COMPRESSION_SERVICE_ID = '3000021'
const DELETE_FILE_SERVICE_ID = '4000002'

// 端侧的任务状态
enum controlsCommand {
  PAUSE = 1,
  RESUME = 2,
  CANCEL = 3,
}

export interface DeviceTransmissionResponse {
  isSuccess: boolean
  RemoteServiceResponse: RemoteServiceResponse | undefined
}

/**
 * 控制设备上的文件操作
 * 1. 从ocs下载文件
 * 2. 将文件上传到ocs
 * 3. 暂停，恢复，取消文件从ocs的下载或文件上传到ocs
 */
export class DeviceTransmissionService {
  private constructor() {
    //do nothing
  }

  static retryInterceptor(attempt: number, args: unknown[]) {
    // 每次重试需要拿到最新的设备对象
    const device = DeviceManager.getDeviceById((args.shift() as IDevice)?.id)

    device && args.unshift(device)

    return args
  }

  static validateResponse(res: AxiosResponse<RemoteServiceResponse> | undefined): DeviceTransmissionResponse {
    return {
      isSuccess: res?.data.statusCode === HTTP_STATUS_OK,
      RemoteServiceResponse: res?.data,
    }
  }

  /**
   * 将指定的ocs的文件下载到设备端指定目录
   * @param device
   * @param fileId 上传到ocs的文件标识
   * @param targetPath  保存到手机里的目录
   * @param notificationId
   * @returns
   */
  @retry(
    DeviceTransmissionService.validateResult,
    DeviceTransmissionService.retryInterceptor,
    TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES
  )
  static async downloadFile(
    device: IDevice,
    fileId: string,
    targetPath: string,
    notificationId: string
  ): Promise<DeviceTransmissionResponse> {
    const res = await device.callService(
      DOWNLOAD_SERVICE_ID,
      {
        params: {
          downloadInfo: {
            fileId,
            targetPath,
            notificationId,
          },
        },
      },
      {}
    )

    return this.validateResponse(res)
  }

  static validateResult(result: unknown) {
    const isCallServiceResult = (result: unknown): result is { isSuccess: boolean } =>
      isPlainObject(result) && 'isSuccess' in (result as object)

    return isCallServiceResult(result) ? result.isSuccess : false
  }

  /**
   * 将设备端指定路劲的文件上传到OCS
   * @param device
   * @param filePath
   * @param notificationId
   * @returns
   */
  @retry(
    DeviceTransmissionService.validateResult,
    DeviceTransmissionService.retryInterceptor,
    TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES
  )
  static async uploadFile(
    device: IDevice,
    filePath: string,
    notificationId: string
  ): Promise<DeviceTransmissionResponse> {
    const res = await device.callService(
      UPLOAD_SERVICE_ID,
      {
        params: {
          uploadInfo: {
            filePath,
            notificationId,
          },
        },
      },
      {}
    )

    return this.validateResponse(res)
  }

  /**
   * 将设备端指定路劲的文件压缩
   * @param device
   * @param GroupFiles[]
   * @param notificationId
   * @returns
   */
  @retry(
    DeviceTransmissionService.validateResult,
    DeviceTransmissionService.retryInterceptor,
    TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES
  )
  static async compress(
    device: IDevice,
    groupFileInfo: GroupFileInfo,
    notificationId: string
  ): Promise<DeviceTransmissionResponse> {
    const res = await device.callService(
      COMPRESSION_SERVICE_ID,
      {
        params: {
          compressionInfo: {
            groupFileInfo,
            notificationId,
          },
        },
      },
      {}
    )

    return this.validateResponse(res)
  }

  @retry(
    DeviceTransmissionService.validateResult,
    DeviceTransmissionService.retryInterceptor,
    TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES
  )
  static async deleteFile(device: IDevice, filePath: string, fileName: string): Promise<DeviceTransmissionResponse> {
    console.log(`[Transmission]: deleteFile ${fileName} ${new Date().toLocaleString()} `)
    const res = await device.callService(
      DELETE_FILE_SERVICE_ID,
      {
        params: {
          operateInfo: {
            operateType: 5,
            sourceFileInfo: [
              {
                data: filePath,
                displayName: fileName,
              },
            ],
            targetFileInfo: {},
            conflictPolicy: '',
            option: 1, // 1表示彻底删除；2表示移至回收站; 3表示在最近删除中删除
          },
        },
      },
      {}
    )

    return this.validateResponse(res)
  }

  @retry(
    DeviceTransmissionService.validateResult,
    DeviceTransmissionService.retryInterceptor,
    TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES
  )
  static async pause(
    device: IDevice,
    noticeId: string,
    isUpload: boolean,
    businessType: TransmissionTaskType
  ): Promise<DeviceTransmissionResponse> {
    const res = await device.callService(
      TRANSMISSION_CONTROL_SERVICE_ID,
      {
        params: {
          controlInfo: {
            noticeId,
            isUpload,
            controlCommand: controlsCommand.PAUSE,
            businessType,
          },
        },
      },
      {}
    )

    return this.validateResponse(res)
  }

  @retry(
    DeviceTransmissionService.validateResult,
    DeviceTransmissionService.retryInterceptor,
    TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES
  )
  static async resume(
    device: IDevice,
    noticeId: string,
    isUpload: boolean,
    businessType: TransmissionTaskType
  ): Promise<DeviceTransmissionResponse> {
    const res = await device.callService(
      TRANSMISSION_CONTROL_SERVICE_ID,
      {
        params: {
          controlInfo: {
            noticeId,
            isUpload,
            controlCommand: controlsCommand.RESUME,
            businessType,
          },
        },
      },
      {}
    )

    return this.validateResponse(res)
  }

  @retry(
    DeviceTransmissionService.validateResult,
    DeviceTransmissionService.retryInterceptor,
    TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES
  )
  static async cancel(
    device: IDevice,
    noticeId: string,
    isUpload: boolean,
    businessType: TransmissionTaskType
  ): Promise<DeviceTransmissionResponse> {
    const res = await device.callService(
      TRANSMISSION_CONTROL_SERVICE_ID,
      {
        params: {
          controlInfo: {
            noticeId,
            isUpload,
            controlCommand: controlsCommand.CANCEL,
            businessType,
          },
        },
      },
      {}
    )

    return this.validateResponse(res)
  }
}
